/**
 * The NOS Deal picker on the New Resource Plan form.
 *
 * Open pipeline Deals from the NOS Gateway, each carrying the amount and the resolved
 * local Solution that picking it sets on the new plan (see
 * src/api/plan-deal-options.ts). Mirrors `useSolutionEstimateProjects`: one query, one
 * key from the factory, a `select` that shapes the response into `SelectOption[]` for
 * `SearchableSelect` — and keeps the Deals by id so the form can apply the one picked.
 */

import { useQuery } from "@tanstack/react-query";
import type { PlanDealOption } from "@/api/plan-deal-options";
import { jsonFetch } from "@/lib/json-fetch";
import { sortOptions } from "@/lib/option-search";
import { planDealOptionKeys } from "@/lib/query";

interface PlanDealOptionsResponse {
  deals: PlanDealOption[];
}

const money = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

function dealLabel(d: PlanDealOption): string {
  const amount = d.amount === null ? "no amount" : money.format(Number(d.amount));
  // Still listed: picking it offers "Create Solution". A Deal whose Solution is in NOS
  // but not yet in Staff gets no note — picking it brings the Solution in.
  const noSolution = d.nymblSolutionId === null ? " · no CRM solution yet" : "";
  return `${d.dealName} · ${amount} · ${d.stage}${noSolution}`;
}

export function usePlanDealOptions() {
  return useQuery({
    queryKey: planDealOptionKeys.all,
    queryFn: () => jsonFetch<PlanDealOptionsResponse>("/api/plan-deal-options"),
    select: (data) => ({
      options: sortOptions(
        data.deals.map((d) => ({ value: d.dealId, label: dealLabel(d), group: d.accountName })),
      ),
      byId: new Map(data.deals.map((d) => [d.dealId, d])),
    }),
  });
}
